import React, { memo, Fragment, useState, useRef, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { styled } from '@compiled/react';

import type { EditorActions } from '@atlaskit/editor-core';
import type { TriggerProps } from '@atlaskit/popup';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';
import ButtonGroup from '@atlaskit/button/button-group';
import Button from '@atlaskit/button/new';
import AkTooltip from '@atlaskit/tooltip/Tooltip';
import type { ADNode } from '@atlaskit/editor-common/validator';
// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer
import Popup from '@atlaskit/popup';
import { layers } from '@atlaskit/theme/constants';
import { token } from '@atlaskit/tokens';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import { WebItemList } from '@confluence/web-item-list/entry-points/WebItemList.ts';
import { EDIT_PAGE_METADATA_RESTRICTIONS_ID } from '@confluence/web-item/entry-points/webItemIDs.ts';
import { SYSTEM_EDITOR_PRECURSOR_BUTTONS } from '@confluence/web-item-location/entry-points/webFragmentLocations.ts';
import {
	WebItemLocation,
	type WebItemLocationChildrenFnWithLoading,
} from '@confluence/web-item-location/entry-points/WebItemLocation.ts';
import { LazyFavouriteButton } from '@confluence/favourite-button/entry-points/FavouriteButton.ts';
import { FavouriteButtonPlaceholder } from '@confluence/favourite-button/entry-points/FavouriteButtonPlaceholder.ts';
import { LazyDialogWrapperLoader } from '@confluence/watch-dialog/entry-points/WatchDialogWrapper.tsx';
import { WATCH_DIALOG_LOCATION } from '@confluence/watch-dialog/entry-points/WatchDialogLocation.tsx';
import { MANAGE_WATCHER_TAB_TYPES } from '@confluence/manage-watcher-dialog/entry-points/manage-watcher-tab-types.ts';
import { WatchButtonPlaceholder } from '@confluence/watch-dialog/entry-points/WatchButtonPlaceholder.js';
import { PublishButtonWithMoreOptions } from '@confluence/publish-modal/entry-points/PublishButtonWithMoreOptions.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import type { PublishProps } from '@confluence/editor-features/entry-points/types.ts';
import { ShareAndRestrictButton } from '@confluence/share-and-restrict-dialog/entry-points/ShareAndRestrictButton.tsx';
import {
	type ShareAndRestrictButtonProps,
	type ContentType,
} from '@confluence/share-and-restrict-dialog/entry-points/ShareRestrictAndCopyCoordinator.tsx';
import { LIVE_PAGE_SUBTYPE } from '@confluence/live-pages-utils';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { CommentIconLoadable } from '@confluence/comment-button/entry-points/CommentIconLoadable.ts';
import { getPlacement } from '@confluence/comment-button/entry-points/CommentButton.ts';
import { inlineCommentGifContent } from '@confluence/inline-comments-common/entry-points/inlineCommentGifContent.ts';
import { EmbeddedPublishButton } from '@confluence/embedded-publish-button';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { AccessStatus } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';
import { CommentButtonPlaceholder } from '@confluence/comment-button/entry-points/CommentButtonPlaceholder.ts';
import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs.ts';
import {
	useCommentsContentState,
	useCommentsContentActions,
} from '@confluence/comment-context/entry-points/useCommentsContent.ts';
import { CommentWarningDialog } from '@confluence/comment-dialogs';
import { fg } from '@confluence/feature-gating';
import { useAnnotations } from '@confluence/annotation-provider-store/entry-points/AnnotationsContext.ts';
import { useSSRPlaceholderReplaceIdProp } from '@confluence/loadable';
import { useShowCommentsPanel } from '@confluence/comments-panel/entry-points/useShowCommentsPanel.ts';
import { PreloadButtonWrapper } from '@confluence/editor-features/entry-points/PreloadButtonWrapper.ts';
import { useIsEmbeddedConfluence_DO_NOT_USE } from '@confluence/embedded-confluence-check';
import { isInPreviewPanel } from '@confluence/preview-panel-api/entry-points/previewPanelUtils.ts';

import { MainButtonContainer, SubsequentNodeWrapper } from './MainButtonStyles.tsx';

const i18n = defineMessages({
	publishExternalShareLabel: {
		id: 'editor-main-buttons.editor.publish.external.share.label',
		defaultMessage: 'Externally shared',
		description: 'The text is shown as a label or badge in the editor publish area when the page is externally shared. It indicates to the user that the current page has external sharing enabled.',
	},
	publishButtonDisableByEmptyTitleTooltip: {
		id: 'editor-main-buttons.editor.buttons.disable.by.empty.title.tooltip',
		defaultMessage: 'Your page needs a title before it can be published.',
		description: 'Tooltip text displayed when publish button is disabled due to empty title',
	},
	publishButtonDisableByNotSavingTooltip: {
		id: 'editor-main-buttons.editor.buttons.disable.by.not.saving.tooltip',
		defaultMessage: 'Still working...',
		description: 'Tooltip text displayed when publish button is disabled due to not saving',
	},
	publishButtonDisableBySavingTooltip: {
		id: 'editor-main-buttons.editor.buttons.disable.by.saving.tooltip',
		defaultMessage: 'We’re still saving your page.',
		description: 'Tooltip text displayed when publish button is disabled due to in saving',
	},
	closeButtonText: {
		id: 'editor-main-buttons.close.name',
		defaultMessage: 'Close',
		description: 'The text is shown on a button in the editor toolbar that closes the editor and returns the user to the view mode of the page.',
	},
	closeButtonPublishTooltip: {
		id: 'editor-main-buttons.editor.close.publish.tooltip',
		defaultMessage: 'Saves changes without publishing',
		description: 'Tooltip text displayed for close button when publishing',
	},
	closeButtonUpdateTooltip: {
		id: 'editor-main-buttons.editor.close.update.tooltip',
		defaultMessage: 'Saves changes without updating',
		description: 'Tooltip text displayed for close button when updating',
	},
	discardOnCloseButtonTooltip: {
		id: 'editor-main-buttons.editor.discard.close.tooltip',
		defaultMessage: 'Discard empty draft',
		description:
			"Tooltip text displayed for close button if it's an empty draft and will get discarded",
	},
	editScheduleTooltip: {
		id: 'editor-main-buttons.edit.schedule.tooltip',
		defaultMessage: 'Edit schedule',
		description: 'Inform user to edit the scheduled publish onclick of the scheduled button',
	},
	emptyBlogPostTitleDefault: {
		id: 'editor-main-buttons.empty.blogPostTitle',
		defaultMessage: 'Untitled blog post',
		description:
			'Default title for untitled blog post drafts. Please match editor.fabric.share.invite.to.edit.empty.blog.post.title in all languages',
	},
	emptyPageTitleDefault: {
		id: 'editor-main-buttons.empty.default-pageTitle',
		defaultMessage: 'Untitled page',
		description:
			'Default title for untitled page drafts. Please match editor-main-buttons.empty.default-pageTitle in all languages',
	},
	emptyLivePageTitleDefault: {
		id: 'editor-main-buttons.empty.default-livePageTitle',
		defaultMessage: 'Untitled live doc',
		description:
			'Default title for untitled live page drafts. Please match editor-main-buttons.empty.default-livePageTitle in all languages',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const WebItemWrapper = styled.span({
	marginRight: token('space.150'),
});

const verticalSeparator = xcss({
	borderLeft: `${token('border.width.selected')} solid ${token('color.border')}`,
	display: 'inline-block',
	width: '1px',
	height: '31px',
	marginBlock: `0`,
	marginInline: 'space.075',
});

export type ComponentProps = {
	/**
	 * Are the main buttons (cancel, publish, meatballs) disabled?
	 */
	areMainButtonsDisabled?: boolean;
	/**
	 * Should the legacy "Publish" button be disabled ? (Only used in embedded editor.)
	 */
	isPublishDisabled: boolean;
	/**
	 * Should the Publish Modal's buttons be disabled (i.e. Update, Publish..., Scheduled) ?
	 * If not provided, isPublishDisabled will be used.
	 */
	isPublishDisabledForPublishModal?: boolean;
	/**
	 * Should the publish and close buttons be hidden? (Only used in embedded editor.)
	 */
	hidePublishCloseButtons?: boolean;
	/**
	 * Should only the close button be hidden? (Only used in embedded editor.)
	 */
	hideCloseButton?: boolean;
	/**
	 * Should the close editor button be disabled?
	 */
	isCloseButtonDisabled?: boolean;
	/**
	 * Optional React node comes before main buttons.
	 */
	precursoryNode?: React.ReactNode;
	/**
	 * Optional React node comes after main buttons.
	 */
	subsequentNode?: React.ReactNode;
	/**
	 * A callback function for clicking Close button
	 */
	onClose: () => void;
	/**
	 * A callback function for clicking Publish button
	 */
	onClickPublish: (arg: PublishProps) => () => void;
	/**
	 * A callback function for clicking the Preview button (used in publish modal)
	 */
	onClickPreview: () => void;
	/**
	 * Flag which identifies whether the title has content
	 */
	publishButtonDisabledReason: string;
	/**
	 * whether to allow web items (restriction)
	 */
	allowWebItems: boolean;
	/**
	 * whether to allow share button
	 */
	hideShare?: boolean;
	/**
	 * content id of a page/blog post
	 */
	contentId: string;
	/**
	 * Describes whether the content is a page or blogpost etc.
	 */
	contentType: string;
	/**
	 * Key of the space this content resides in
	 */
	spaceKey: string;
	/**
	 * Content is a draft
	 */
	isUnpublishedDraft: boolean;
	/**
	 * Indicates whether a publish is currently in progress
	 */
	isPublishingContent: boolean;
	/**
	 * Whether the close button and its tooltip will show that the draft is being discarded (true if draft is blank)
	 */
	discardOnCloseEnabled?: boolean;
	/**
	 * Function used within the publish modal to handle page to blog conversions.
	 */
	retrieveEditorTitleAndContent: () => Promise<{
		title?: string | null | undefined;
		adf: ADNode;
		syncRev?: string;
		ncsStepVersion?: string;
	}>;
	/**
	 * Indicates whether the editor is in preview mode. If it is, then the publish button/modal should not be displayed.
	 */
	shouldRenderPreview: boolean;
	/**
	 * Flags for use within the publish modal to handle any blog conversion errors.
	 */
	flags: FlagsStateContainer;
	/**
	 * Function used for saving the editor content to confluence backend
	 */
	saveDraft: () => void;
	/**
	 * Boolean to determine whether to render embedded publish button or publish button with more options
	 */
	useEmbeddedPublishButton?: boolean;

	/**
	 * Boolean to determine whether to render embedded publish button with the publish modal or the normal publish button with more options
	 */
	useEmbeddedPublishModal?: boolean;

	// To be cleaned up
	isPublishModalEnabled?: boolean;

	/**
	 * Page's title
	 */
	title: string | null;
	allowInlineComments?: boolean;
	allowPageComments?: boolean;
	allowStar?: boolean;
	allowWatch?: boolean;
	allowCopyLink?: boolean;
	allowRestrictions?: boolean;
	editorActions?: EditorActions;
	viewMode?: boolean;
};

const publishButtonDisableReasonToTranslation = {
	'editor.buttons.disable.by.saving.tooltip': 'publishButtonDisableBySavingTooltip',
	'editor.buttons.disable.by.not.saving.tooltip': 'publishButtonDisableByNotSavingTooltip',
	'editor.buttons.disable.by.empty.title.tooltip': 'publishButtonDisableByEmptyTitleTooltip',
};

export const MainButtons: React.FC<ComponentProps> = memo(
	({
		areMainButtonsDisabled,
		subsequentNode,
		publishButtonDisabledReason,
		precursoryNode,
		isPublishDisabled,
		isPublishDisabledForPublishModal,
		hidePublishCloseButtons,
		hideCloseButton,
		onClickPublish,
		onClose,
		onClickPreview,
		allowWebItems,
		hideShare,
		contentId,
		spaceKey,
		isUnpublishedDraft: isUnpublishedDraftProp,
		isPublishingContent,
		discardOnCloseEnabled,
		retrieveEditorTitleAndContent,
		shouldRenderPreview,
		flags,
		saveDraft,
		useEmbeddedPublishButton,
		useEmbeddedPublishModal,
		contentType,
		title,
		allowInlineComments = true,
		allowPageComments = true,
		allowStar = false,
		allowWatch = false,
		allowRestrictions = true,
		viewMode,
		isCloseButtonDisabled,
	}) => {
		const isLivePage = useIsCurrentPageLive();
		const isEmbeddedConfluence = useIsEmbeddedConfluence_DO_NOT_USE();

		const { showModal } = useDialogs();

		const { editorCreateProps } = useAnnotations();
		const { hasContentChanged } = useCommentsContentState();
		const { resetContentChanged } = useCommentsContentActions();

		const { accessStatus } = useSessionData();

		const [isDialogOpen, setDialogState] = useState(false);
		const commentButtonRef = useRef<HTMLDivElement | null>(null);
		const ssrPlaceholderIdProp = useSSRPlaceholderReplaceIdProp();

		// When publishing an unpublished draft, the editor receives the PubSub event from the publish event
		// before the publish animation completes and closes the editor.
		// This causes distracting changes in the MainButtons that we want to stop.
		// To prevent them, we only allow isUnpublishedDraft to be updated when isPublishingContent is false.
		const [isUnpublishedDraft, setIsUnpublishedDraft] = useState(isUnpublishedDraftProp);
		useEffect(() => {
			if (!isPublishingContent) {
				setIsUnpublishedDraft(isUnpublishedDraftProp);
			}
		}, [isPublishingContent, isUnpublishedDraftProp]);

		const { isCommentsPanelShown, hideCommentsPanel, showCommentsPanel } = useShowCommentsPanel();

		const intl = useIntl();
		const publishButtonDisabledTooltip = publishButtonDisabledReason
			? intl.formatMessage(
					i18n[publishButtonDisableReasonToTranslation[publishButtonDisabledReason]],
				)
			: '';
		const getPublishButton = () => {
			if (useEmbeddedPublishButton && !(useEmbeddedPublishModal && isUnpublishedDraft)) {
				return (
					<EmbeddedPublishButton
						onClickPublish={onClickPublish}
						publishButtonDisabled={isPublishDisabled || areMainButtonsDisabled}
						savingInProgress={isPublishingContent}
						isUnpublishedDraft={isUnpublishedDraft}
					/>
				);
			}

			const shouldShowFooterButtons = !(
				useEmbeddedPublishButton &&
				useEmbeddedPublishModal &&
				isUnpublishedDraft
			);

			return (
				<PublishButtonWithMoreOptions
					onClickPublish={onClickPublish}
					publishButtonDisabled={isPublishDisabledForPublishModal ?? isPublishDisabled}
					title={title}
					retrieveEditorTitleAndContent={retrieveEditorTitleAndContent}
					userViewingPreview={shouldRenderPreview}
					onClickPreview={onClickPreview}
					isUnpublishedDraft={isUnpublishedDraft}
					flags={flags}
					saveDraft={saveDraft}
					contentType={contentType}
					savingInProgress={!!isPublishingContent}
					shouldShowWorldIcon={false}
					shouldShowFooterButtons={shouldShowFooterButtons}
				/>
			);
		};

		const getCloseButtonTooltip = (): string | undefined => {
			if (areMainButtonsDisabled) {
				return undefined;
			} else if (discardOnCloseEnabled) {
				return intl.formatMessage(i18n.discardOnCloseButtonTooltip);
			} else if (isUnpublishedDraft) {
				return intl.formatMessage(i18n.closeButtonPublishTooltip);
			} else {
				return intl.formatMessage(i18n.closeButtonUpdateTooltip);
			}
		};

		const CloseButton = () => (
			<AkTooltip content={getCloseButtonTooltip}>
				<Button
					id="close-button"
					data-test-id="close-button"
					appearance="subtle"
					onClick={onClose}
					isDisabled={isCloseButtonDisabled}
				>
					{intl.formatMessage(i18n.closeButtonText)}
				</Button>
			</AkTooltip>
		);

		const copyLinkButtonSource: { source: ShareAndRestrictButtonProps['source'] } = {
			source: 'editToolbar',
		};

		const checkUnsavedAndHandleCommentButtonClick = () => {
			// With the way the editor annotation provider works, it will show BOTH the the view components
			// at the same time, so if the create component is shown, ignore any changes and just trigger
			// the button press to select thefirst (or first unread) comment and let the closing of the create
			// flow handle the content changed checks and reset the changed values, this will only handle view
			if (!editorCreateProps && hasContentChanged) {
				showModal(CommentWarningDialog, {
					onConfirm: () => {
						resetContentChanged();
						handleCommentIconClickInEditor();
					},
				});
			} else {
				handleCommentIconClickInEditor();
			}
		};

		const handleCommentIconClickInEditor = () => {
			if (isCommentsPanelShown) {
				hideCommentsPanel();
			} else {
				showCommentsPanel();
			}
		};

		const getCommentIcon = (triggerProps: TriggerProps) => {
			return (
				<CommentIconLoadable
					triggerProps={triggerProps}
					intl={intl}
					handleCommentButtonClick={
						fg('confluence_frontend_editor_annotation_manager')
							? checkUnsavedAndHandleCommentButtonClick
							: handleCommentIconClickInEditor
					}
					commentButtonRef={commentButtonRef}
					isDisabled={areMainButtonsDisabled}
					contentId={contentId}
					pageMode="edit"
				/>
			);
		};

		const webItems: React.ReactElement[] = [];

		const commentResidesInPreviewPanel = isInPreviewPanel();

		if (allowWebItems) {
			webItems.push(
				<WebItemWrapper data-test-id="editor-precursor-buttons-wrapper" key={webItems.length}>
					<WebItemLocation
						key="precursor-buttons"
						tagName="span"
						contentId={contentId}
						location={SYSTEM_EDITOR_PRECURSOR_BUTTONS}
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
						style={{ display: 'flex' }}
						renderWhenLoading
					>
						{
							// By default, there should be one web item, the restrictions web item. Additional web items may exist on tenants with Connect apps.
							(({ loading, webItems: loadedWebItems }) => {
								// If we're a live page and there are no web items (e.g. live view), we still always want to show the default buttons.
								if (loading || (!loadedWebItems.length && !isLivePage)) {
									return (
										<Fragment>
											{allowInlineComments &&
												allowPageComments &&
												!commentResidesInPreviewPanel && <CommentButtonPlaceholder />}
											{allowStar && <FavouriteButtonPlaceholder />}
											{allowWatch && <WatchButtonPlaceholder />}
										</Fragment>
									);
								}

								// The restrictions button is rendered via <RestrictionsDialogTriggerWrapper> instead of as a web item, to fix positioning issue CCP-871.
								// If in view mode (live view), do not render any web items as they are intended for users editing the page.
								const filteredWebItems =
									loading || viewMode
										? []
										: loadedWebItems.filter(({ id }) => id !== EDIT_PAGE_METADATA_RESTRICTIONS_ID);

								return (
									<Fragment>
										{allowInlineComments && allowPageComments && !commentResidesInPreviewPanel && (
											<ErrorBoundary attribution={Attribution.COLLABORATION}>
												<Popup
													isOpen={isDialogOpen}
													content={inlineCommentGifContent}
													trigger={(triggerProps) => getCommentIcon(triggerProps)}
													placement={getPlacement(commentButtonRef.current)}
													onClose={() => {
														setDialogState(false);
													}}
													zIndex={layers.flag()}
													shouldRenderToParent
												/>
											</ErrorBoundary>
										)}
										{allowStar && (
											<LazyFavouriteButton
												key="favourite-button"
												contentId={contentId}
												isDisabled={areMainButtonsDisabled}
											/>
										)}
										{allowWatch && (
											<LazyDialogWrapperLoader
												key="watch-dialog"
												contentId={contentId}
												defaultTab={MANAGE_WATCHER_TAB_TYPES.PAGE}
												componentLocation={WATCH_DIALOG_LOCATION.PAGE}
												isDisabled={areMainButtonsDisabled}
											/>
										)}
										{/* Other add-ons */}
										{filteredWebItems.length && !isLivePage ? (
											<ErrorBoundary attribution={Attribution.ECOSYSTEM}>
												<WebItemList
													id="editor-precursor-buttons"
													dataTestId="editor-metadata-web-items"
													webItems={filteredWebItems}
												/>
											</ErrorBoundary>
										) : null}
									</Fragment>
								);
							}) as WebItemLocationChildrenFnWithLoading
						}
					</WebItemLocation>
				</WebItemWrapper>,
			);
		}

		// Live pages render share button instead of publish and close buttons
		if (isLivePage) {
			return (
				<MainButtonContainer data-vc="editor-main-buttons-container" {...ssrPlaceholderIdProp}>
					{webItems}
					{precursoryNode || null}
					<ButtonGroup>
						<ShareAndRestrictButton
							contentId={contentId}
							contentType={contentType as ContentType}
							contentSubType={LIVE_PAGE_SUBTYPE}
							shareeAction="view"
							allowRestrictions={allowRestrictions}
							{...copyLinkButtonSource}
						/>
					</ButtonGroup>
					{subsequentNode ? (
						<SubsequentNodeWrapper data-test-id="subsequent-node-wrapper">
							{subsequentNode}
						</SubsequentNodeWrapper>
					) : null}
				</MainButtonContainer>
			);
		} else {
			const publishButton = getPublishButton();
			const shouldHideCloseButton = isEmbeddedConfluence && hideCloseButton;

			const publishButtonGroup = (
				<ButtonGroup>
					{publishButton}
					{!shouldHideCloseButton && <CloseButton />}
				</ButtonGroup>
			);

			const preloadedPublishButton = (
				<ButtonGroup>
					<PreloadButtonWrapper
						isPublish
						contentId={contentId}
						spaceKey={spaceKey}
						contentType={contentType}
						isUnpublishedDraft={isUnpublishedDraft}
					>
						{publishButton}
					</PreloadButtonWrapper>
					{!shouldHideCloseButton && (
						<PreloadButtonWrapper
							isPublish={false}
							contentId={contentId}
							spaceKey={spaceKey}
							contentType={contentType}
							isUnpublishedDraft={isUnpublishedDraft}
						>
							<CloseButton />
						</PreloadButtonWrapper>
					)}
				</ButtonGroup>
			);

			const shouldShowPublishCloseButtons = isEmbeddedConfluence ? !hidePublishCloseButtons : true;

			return (
				<MainButtonContainer data-vc="editor-main-buttons-container" {...ssrPlaceholderIdProp}>
					{webItems}
					{precursoryNode || null}
					{areMainButtonsDisabled ? (
						<AkTooltip content={publishButtonDisabledTooltip} position="bottom">
							{shouldShowPublishCloseButtons && publishButtonGroup}
						</AkTooltip>
					) : (
						<>{shouldShowPublishCloseButtons && preloadedPublishButton}</>
					)}
					{accessStatus !== AccessStatus.EXTERNAL_COLLABORATOR_ACCESS && !hideShare && (
						<>
							<Box xcss={verticalSeparator} />
							<ShareAndRestrictButton
								contentId={contentId}
								contentType={contentType as ContentType}
								shareeAction="edit"
								allowRestrictions={allowRestrictions}
								isUnpublishedDraft={isUnpublishedDraft}
								{...copyLinkButtonSource}
							/>
						</>
					)}
					{subsequentNode ? (
						<SubsequentNodeWrapper data-test-id="subsequent-node-wrapper">
							{subsequentNode}
						</SubsequentNodeWrapper>
					) : null}
				</MainButtonContainer>
			);
		}
	},
);
